import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpRight } from 'lucide-react';
import {
    ActivationCounts,
    PeopleCounts,
    UserActivityCounts,
    CoverageHistory,
    AnalyticsPeople,
} from 'src/domain/analytics';
import { Section } from 'src/components/Section';
import { MetricCards } from './MetricCards';
import { AnalyticsCard } from './AnalyticsCard';
import { TrackedActivitySection } from './TrackedActivitySection';
import { useAnalyticsSupportDrilldown } from './useAnalyticsSupportDrilldown';
import { useScopedAnalyticsText } from './analyticsScope';

const WITHOUT_RELATIVES_PATH =
    '/app/users?sort=mainContacts:asc&highlight=withoutRelatives';

export const PeopleSummary = ({
    people,
    organizations,
    scopeCount,
    updated,
    usage,
    coverageHistory,
    historyLoading = false,
    historyError,
    departmentFiltered = false,
}: {
    people: PeopleCounts;
    organizations: AnalyticsPeople['organizations'];
    /** First staff card: organizations on the global dashboard, departments inside one. */
    scopeCount: { label: string; value: number };
    updated: string;
    usage?: UserActivityCounts;
    coverageHistory?: CoverageHistory;
    historyLoading?: boolean;
    historyError?: unknown;
    departmentFiltered?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const text = useScopedAnalyticsText();
    const number = (value: number) => value.toLocaleString(i18n.language);
    const ratio = (links: number) =>
        people.residents
            ? number(Math.round((links / people.residents) * 10) / 10)
            : '—';
    const activation = (counts: ActivationCounts) =>
        t('admin.analytics.activationDetail', {
            activated: number(counts.activated),
            never: number(counts.neverLoggedIn),
            unknown: number(counts.unknown),
        });

    const drilldown = useAnalyticsSupportDrilldown(organizations);
    const withoutRelatives = people.residentCoverage[0] ?? 0;
    const canOpenWithoutRelatives =
        drilldown.canOpen && !departmentFiltered && withoutRelatives > 0;
    const openWithoutRelatives = () =>
        drilldown.open({
            path: WITHOUT_RELATIVES_PATH,
            noteKey: 'admin.analytics.openWithoutRelativesInSupport',
            counts: Object.fromEntries(
                organizations.map(org => [
                    org.id,
                    org.people.residentCoverage[0] ?? 0,
                ]),
            ),
        });
    const staffHeading = useId();
    const residentHeading = useId();
    const mainContactAccounts = people.mainContactAccounts;
    const residentsWithMainContact = people.residentsWithMainContact;
    const roleRows = (rows: readonly (readonly [string, number])[]) => (
        <dl className="analytics-detail-list">
            {rows.map(([key, count]) => (
                <div key={key}>
                    <dt>{t(`admin.analytics.${key}`)}</dt>
                    <dd>{number(count)}</dd>
                </div>
            ))}
        </dl>
    );
    return (
        <Section
            className="analytics-section"
            title={t('admin.analytics.people')}
            action={
                <p className="analytics-note" aria-live="polite">
                    {updated}
                </p>
            }
        >
            <div className="analytics-people-groups">
                <section
                    className="analytics-people-group analytics-staff-group"
                    aria-labelledby={staffHeading}
                >
                    <h3 id={staffHeading} className="analytics-group-title">
                        {text('admin.analytics.organizationsAndStaff')}
                    </h3>
                    <div className="analytics-staff-metrics">
                        <MetricCards
                            metrics={[
                                {
                                    label: scopeCount.label,
                                    value: number(scopeCount.value),
                                },
                                {
                                    label: t('admin.analytics.employees'),
                                    value: number(
                                        people.totalEmployees ??
                                            people.employees.total,
                                    ),
                                    detail: t(
                                        'admin.analytics.employeeTypeBreakdown',
                                        {
                                            fullTime: number(
                                                people.employees.total,
                                            ),
                                            partTime: number(
                                                (people.totalEmployees ??
                                                    people.employees.total) -
                                                    people.employees.total,
                                            ),
                                        },
                                    ),
                                    explanation: (
                                        <>
                                            {t('admin.analytics.fullTimeScope')}
                                            <br />
                                            {activation(people.employees)}
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </div>
                    <AnalyticsCard
                        title={t('admin.analytics.permissions')}
                        description={t('admin.analytics.permissionsNote')}
                    >
                        {roleRows([
                            ['organizationAdmins', people.organizationAdmins],
                            ['employeeManagers', people.employeeManagers],
                            ['broadcasters', people.broadcasters],
                        ])}
                    </AnalyticsCard>
                </section>
                <section
                    className="analytics-people-group analytics-resident-group bg-lightBlue"
                    aria-labelledby={residentHeading}
                >
                    <h3 id={residentHeading} className="analytics-group-title">
                        {t('admin.analytics.residentsAndRelatives')}
                    </h3>
                    <div className="analytics-resident-metrics">
                        <MetricCards
                            metrics={[
                                {
                                    label: t('admin.analytics.residents'),
                                    value: number(people.residents),
                                    explanation:
                                        residentsWithMainContact !== undefined
                                            ? t(
                                                  'admin.analytics.residentsWithMainContactCount',
                                                  {
                                                      count: residentsWithMainContact,
                                                      total: people.residents,
                                                  },
                                              )
                                            : undefined,
                                },
                                {
                                    label: t('admin.analytics.mainContacts'),
                                    value: number(people.mainContacts),
                                    explanation: (
                                        <>
                                            {t(
                                                'admin.analytics.mainContactsScope',
                                            )}
                                            <br />
                                            {mainContactAccounts &&
                                                activation(
                                                    mainContactAccounts,
                                                )}{' '}
                                            {t(
                                                'admin.analytics.mainContactsPerResident',
                                                {
                                                    value: ratio(
                                                        people.mainContactLinks,
                                                    ),
                                                },
                                            )}
                                        </>
                                    ),
                                },
                                {
                                    label: t('admin.analytics.guardians'),
                                    value: number(people.guardians),
                                    explanation: (
                                        <>
                                            {t('admin.analytics.guardianNote')}
                                            <br />
                                            {t('admin.analytics.perResident', {
                                                value: ratio(
                                                    people.guardianLinks,
                                                ),
                                            })}
                                        </>
                                    ),
                                },
                                {
                                    label: t('admin.analytics.relatives'),
                                    value: number(people.relatives.total),
                                    explanation: (
                                        <>
                                            {t(
                                                'admin.analytics.relativeTypeBreakdown',
                                                {
                                                    mainContacts: number(
                                                        people.mainContacts,
                                                    ),
                                                    others: number(
                                                        people.relatives.total -
                                                            people.mainContacts,
                                                    ),
                                                },
                                            )}
                                            {people.relativeLinks !==
                                                undefined && (
                                                <>
                                                    <br />
                                                    {t(
                                                        'admin.analytics.perResident',
                                                        {
                                                            value: ratio(
                                                                people.relativeLinks,
                                                            ),
                                                        },
                                                    )}
                                                </>
                                            )}
                                        </>
                                    ),
                                },
                            ]}
                        />
                    </div>
                    <AnalyticsCard
                        title={t('admin.analytics.residentConnections')}
                        description={t('admin.analytics.coverageNote')}
                    >
                        <dl className="analytics-detail-list">
                            {[3, 2, 1, 0].map(index => (
                                <div key={index}>
                                    <dt>
                                        {t('admin.analytics.linkedRelatives', {
                                            count: index,
                                            label: index === 3 ? '3+' : index,
                                        })}
                                    </dt>
                                    <dd>
                                        {index === 0 &&
                                        canOpenWithoutRelatives ? (
                                            <button
                                                type="button"
                                                className="analytics-drilldown-button analytics-drilldown-accent"
                                                onClick={openWithoutRelatives}
                                                aria-label={t(
                                                    'admin.analytics.viewResidentsWithoutRelatives',
                                                )}
                                                title={t(
                                                    'admin.analytics.viewResidentsWithoutRelatives',
                                                )}
                                            >
                                                {number(withoutRelatives)}
                                                <ArrowUpRight
                                                    size={13}
                                                    strokeWidth={2.25}
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        ) : (
                                            number(
                                                people.residentCoverage[index],
                                            )
                                        )}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                        <div className="analytics-relative-roles">
                            <h4>{t('admin.analytics.relativeRoles')}</h4>
                            {roleRows([
                                ['familyRelatives', people.familyRelatives],
                                ['externalRelatives', people.externalRelatives],
                            ])}
                        </div>
                    </AnalyticsCard>
                </section>
            </div>
            {drilldown.chooser}
            <TrackedActivitySection
                organizations={organizations}
                usage={usage}
                employees={people.employees.total}
                residents={people.residents}
                history={coverageHistory}
                loading={historyLoading}
                error={historyError}
                departmentFiltered={departmentFiltered}
            />
        </Section>
    );
};
